import _ from 'lodash';
import {safeMult, safeAddNoFixed, safeAdd} from 'common/utils';
import {
  BOCS,
  ADDITIONAL_INFORMATION,
  COST,
  QUANTITY,
  PRICE,
  ACTUAL_QUANTITY,
  ACTUAL_PRICE,
  SCOPE_OF_WORK_TOTAL,
  ALLOWED_EXCESS_TOTAL,
  ACTUAL_TOTAL,
} from 'common/requestDetails/constants.js';

export const INDIRECT_CHARGES_BOCS = ['indirectCharges'];
export const DIRECT_CHARGES_BOCS = _.difference(BOCS, INDIRECT_CHARGES_BOCS);

/* 
Terminology.  
costItemObject - a leaf node JSON object in fundingStreams hierarchy that contains a "cost" and "additionalInformation" property
periodCostArray - one of the nested arrays within the outer "cost" array representing costs of an item for a period
costObject - one of the objects contained within a periodCostArray

A "PrepFire Cost Items" confluence page is being crated to discuss cost items and the need for this class.
At the time of this writing it is under 
Produce > FEMA GO Features > Technical Enablers > Cost share and budget calculations refactor > Design Approach >
  > PrepFire Budget Calculation Refactoring > PrepFire Cost Items
https://maestro.dhs.gov/confluence/spaces/GMMGO/pages/1848053361/PrepFire+Cost+Items

The fundingStreams array property in the payload of grant entity contains fundingStream objects that have a 
hierarchy of children objects and the leaf nodes will be an object containing a "cost" property and an 
"additionalInformation" property.  These days they will have random time-based keys such as
g1766353709250-1de4aa274a6d66b3-*.  Such objects are a costItemObject or costItemObj. Most of the time 
the cost data is in the "cost" property which is an array of periodCostArrays each of which contain one
or more cost objects, costObj.
g1766353709250-costItemObj: {}
  cost: [
    [{*costObj*}, ...], // period 1 periodCostArray
    [{*costObj*}, ...], // period 2 periodCostArray
    ...
  ],
  additionalInformation: {...}
}
The fpsFundingStreams1 variable defined in fpsFundingStreams1.js and imported into the unit test of this class
annotates the JSON w/ those terms above. 

A cost object, costObj, typically looks something like
{boc: "equipment", quantity: 2, price: "120.00", "total": 240}

When submitting a closeout report you enter actual values and those values are typically stored within that costObj
{boc: "eqipment", quantity: 2, price: "120.00", "total": 240, actualQuanity: 3, actualPrice: "110", ...}

During programmatic closeout review the reviewer can mark an actual cost as not being totally within scope
and can then specify the amount that is within scope, an amount they they will allow as excess, and
and amount they will disallow
{
  boc: "eqipment", quantity: 2, price: "120.00", "total": 240, 
  actualQuanity: 3, actualPrice: "110", actualTotal: 330, ,...,
  scopeOfWorkTotal: "110.00", allowedExcessTotal: "110.00", disallowedTotal: "110.00", ...
}
Before closeout the value of a costObj is its quantity * price.  During close report submission
we need to perform calculations on actual values so a costObj value in that context is
actualQuantity * actualPrice.   And finally in the context of programmatic closeout review,
we want to interpret the value of a costObj as its "allowable cost" which is calculated
using scopeOfWorkTotal + allowedExcessTotal
 
The CostItemTotaler is a class that is instantiated with a totalerType argument to the constructor.
The totalerType indicates how the class will interpret a costItemObj and its costObj.  It can have
a value of
  * default - when totaling costs before closeout
  * actuals - typical interpretation of actual costs used during closeout report submission
  * allowables - typical interpretation of allowable costs used during programmatic closeout review
  * actualsHiring - when totaling actual values for a hiringOfFirefighters funding stream
  * allowablesHiring - when totalling allowable values for hiringOfFirefighters funding stream

If no constructor argument is provided totalType defaults to "default".

There are functions to evaluate a costObj as an original (default) value, an actual value, or
allowable value; functions to sum up such costObj values that exist in a periodCostArray,
(SAFER is only one with multiple costObj values per period), and functions to sum up a whole
tree of (eligible) costItemObjs by period.  Calculation of budget.budgetSummary is done in
this class as well (buildBudgetSummary(numPeriods, fundingStreams))
 */
class CostItemObjectTotaler {
  constructor(totalerType) {
    let type = totalerType ? totalerType : 'default';
    this.totalerType = type;
    this.costItemObjectValueFunc = this.costItemObjectValueFuncMap[type];
    this.costObjValueFunc = this.costObjValueFuncMap[type];
    if (!this.costItemObjectValueFunc || !this.costObjValueFunc)
      throw new Error(`bad totalerType provided: ${totalerType}`);
  }

  computeCostObjectValue(costObj) {
    return this.costObjValueFunc(costObj);
  }

  computeCostItemObjectValue(costItemObj) {
    return this.costItemObjectValueFunc(costItemObj);
  }

  /**
   * Return quantity*price of given cost object
   * When costObj = {boc: 'contractual', quantity: 4, price: '1000.00', total: 4000}
   * Then return 4*1000.00 or 4000... it ignores the total property
   * @param {*} costObj
   * @returns numeric value of costObj
   */
  defaultCostObjValue = (costObj) => {
    const q = _.get(costObj, QUANTITY, 0);
    const p = _.get(costObj, PRICE, 0);
    const costObjValue = safeMult(q, p); // returns null if any args are not a number
    return costObjValue === null ? 0 : costObjValue;
  };

  /**
   * Return actual value (actualQuantity*actualPrice) of given cost object
   * When costObj = {..., actualQuanity: 10, actualPrice: "20.01", ...}
   * Then return 10*20.01 or 200.10... it ignores the actualTotal property
   * @param {*} costObj
   * @returns numeric value of costObj
   */
  actualsCostObjValue = (costObj) => {
    const q = _.get(costObj, ACTUAL_QUANTITY, 0);
    const p = _.get(costObj, ACTUAL_PRICE, 0);
    const costObjValue = safeMult(q, p); // returns null if any args are not a number
    return costObjValue === null ? 0 : costObjValue;
  };

  /**
   * Return allowable value, scopeOfWorkTotal+allowedExcessTotal, of given cost object
   * if both those fields exist, and parse into something other than NaN, otherwise
   * use actualQuantity*actualPrice if both of those are defined and parse into
   * something other than NaN, otherwise return 0.
   *
   * Allowable costs appeaer in Programmatic Closeout Review, but only once there
   * is some interaction with Award costs such as when top-level accordion gets
   * expanded.  If that hasn't occurred the allowable files will be undefined.
   * In that case, fall back to using actual costs, which is what allowable
   * cost defaults to anyway... when the  allowable cost fields are initialized
   * the scopeOfWorkTotal equals actual quantity times actual price, and the other two fields,
   * allowedExcessTotal and disallowedTotal, are 0.
   * @param {*} costObj
   * @returns numeric value of costObj
   */
  allowablesCostObjValue = (costObj) => {
    /*
     * global isNaN(o) and Number.isNaN(o) can return false,
     * yet when o is parsed it results in NaN.
     * isNaN(null) = false, isNaN( parseFloat(null) ) = true
     */
    const inScope = parseFloat(_.get(costObj, SCOPE_OF_WORK_TOTAL));
    const allowedExcess = parseFloat(_.get(costObj, ALLOWED_EXCESS_TOTAL));
    const actualQuantity = parseInt(_.get(costObj, ACTUAL_QUANTITY));
    const actualPrice = parseFloat(_.get(costObj, ACTUAL_PRICE));
    let costObjValue;
    if (!Number.isNaN(inScope) && !Number.isNaN(allowedExcess)) {
      // good allowables fields
      costObjValue = safeAddNoFixed(inScope, allowedExcess);
    } else if (!Number.isNaN(actualQuantity) && !Number.isNaN(actualPrice)) {
      // good actuals fields
      costObjValue = safeMult(actualQuantity, actualPrice);
    } else {
      // something off, so return 0
      costObjValue = 0;
    }
    return costObjValue;
  };

  /**
   * A no-op function that always returns 0 regardless of costObj parameter.
   * Used by computeCostObjectValue(costObj) for actualsHiring and allowablesHiring
   * totalers which do not store they actual or allowable values in costObj fields.
   * @param {*} costObj
   * @returns
   */
  zeroCostObjValue = (costObj) => 0;

  /**
   * This function is used by defaultCostItemObjTotaler(...), actualsCostItemObjTotaler(...),
   * and allowablesCostItemObjtotaler(...) to return total for each period associated with
   * the costItemObj.
   * @param {*} costItemObj - cost item for which to get period totals
   * @param {*} costObjValueFunc - function to use to get value of costObj in periodCostArray
   * @returns array with a numeric values representing a total of this costItemObj
   * for each period, using the costObjValueFunc provided to sum up costObjs
   */
  computeCostItemObjPeriodTotalsFromCostProperty(costItemObj, costObjValueFunc) {
    const cost = _.get(costItemObj, COST); // array with nested arrays for each period
    const costItemPeriodTotals = _.reduce(
      cost,
      (periodTotals, periodCostArray, p) => {
        const sumOfCostObjs = _.reduce(
          periodCostArray,
          (periodTotal, costObj) => {
            const costObjValue = costObjValueFunc(costObj);
            periodTotal = safeAddNoFixed(periodTotal, costObjValue);
            return periodTotal;
          },
          0
        );
        periodTotals[p] = sumOfCostObjs;
        return periodTotals;
      },
      []
    );
    return costItemPeriodTotals;
  }

  /**
   * Compute the original (default) period values for the specified costItemObj.
   * @param {*} costItemObj
   * @returns array of period totals for costItemObj
   */
  computeDefaultCostItemObjPeriodTotals = (costItemObj) => {
    /* sum up quantity*proces in each period array */
    const costItemPeriodTotals = this.computeCostItemObjPeriodTotalsFromCostProperty(
      costItemObj,
      this.defaultCostObjValue
    );
    return costItemPeriodTotals;
  };

  /**
   * Compute the actuals period values for the specified costItemObj.
   * @param {*} costItemObj
   * @returns array of period totals for costItemObj
   */
  computeActualsCostItemObjPeriodTotals = (costItemObj) => {
    /* sum up actualQuantity*actualProces in each period array */
    const costItemPeriodTotals = this.computeCostItemObjPeriodTotalsFromCostProperty(
      costItemObj,
      this.actualsCostObjValue
    );
    return costItemPeriodTotals;
  };

  /**
   * Compute the allowables period values for the specified costItemObj.
   * @param {*} costItemObj
   * @returns array of period totals for costItemObj
   */
  computeAllowablesCostItemObjPeriodTotals = (costItemObj) => {
    /* sum up scopeOfWorkTotal+allowedExcessTotal in each period array to get allowed amount for period */
    // { cost: [ [{..., "scopeOfWorkTotal": "50.00","allowedExcessTotal": "25.00", "disallowedTotal": "25.00"}], [{...}]]}
    const costItemPeriodTotals = this.computeCostItemObjPeriodTotalsFromCostProperty(
      costItemObj,
      this.allowablesCostObjValue
    );
    return costItemPeriodTotals;
  };

  /**
   * Compute the actuals period values for the specified costItemObj of hiringOfFireFighters costItemObj.
   * @param {*} costItemObj
   * @returns array of period totals for costItemObj,
   * costItemObj.additionalInformation.actualTotal array as array of numbers.
   */
  computeActualsHiringCostItemObjPeriodTotals = (costItemObj) => {
    const actualTotal = _.get(
      costItemObj,
      `${ADDITIONAL_INFORMATION}.${ACTUAL_TOTAL}`,
      []
    );
    const periodTotals = _.map(actualTotal, (totalStr) => {
      const total = parseFloat(totalStr);
      return Number.isNaN(total) ? 0 : total;
    });
    return periodTotals;
  };

  /**
   * Compute the actuals period values for the specified costItemObj of hiringOfFireFighters costItemObj.
   * @param {*} costItemObj
   * @returns array of period totals for costItemObj,
   * costItemObj.additionalInformation.actualTotal array as array of numbers.
   */
  computeAllowablesHiringCostItemObjPeriodTotals = (costItemObj) => {
    const scopeOfWorkTotals = _.get(
      costItemObj,
      `${ADDITIONAL_INFORMATION}.${SCOPE_OF_WORK_TOTAL}`,
      []
    );
    const allowedExcessTotals = _.get(
      costItemObj,
      `${ADDITIONAL_INFORMATION}.${ALLOWED_EXCESS_TOTAL}`,
      []
    );
    const actualTotals = _.get(
      costItemObj,
      `${ADDITIONAL_INFORMATION}.${ACTUAL_TOTAL}`,
      []
    );

    let periodTotals, inScopeTotals;
    if (scopeOfWorkTotals && scopeOfWorkTotals.length > 0) {
      inScopeTotals = scopeOfWorkTotals;
    } else {
      //fall back to using actuals
      inScopeTotals = actualTotals;
    }
    periodTotals = _.reduce(
      inScopeTotals,
      (perTotals, inScopeTotal, p) => {
        const allowedExcessTotal = allowedExcessTotals[p];
        const perTotal = safeAddNoFixed(inScopeTotal, allowedExcessTotal);
        perTotals[p] = perTotal;
        return perTotals;
      },
      []
    );
    return periodTotals;
  };

  costItemObjectValueFuncMap = {
    default: this.computeDefaultCostItemObjPeriodTotals,
    actuals: this.computeActualsCostItemObjPeriodTotals,
    allowables: this.computeAllowablesCostItemObjPeriodTotals,
    actualsHiring: this.computeActualsHiringCostItemObjPeriodTotals,
    allowablesHiring: this.computeAllowablesHiringCostItemObjPeriodTotals,
  };

  costObjValueFuncMap = {
    default: this.defaultCostObjValue,
    actuals: this.actualsCostObjValue,
    allowables: this.allowablesCostObjValue,
    actualsHiring: this.zeroCostObjValue,
    allowablesHiring: this.zeroCostObjValue,
  };

  /**
   * Return array of numeric values representing the total cost for each period of the costItemObjects
   * found under the costItemTree.
   */
  sumCostItemObjectsInTree(numPeriods, costItemTree) {
    /* collect array of costItemObjects [ {cost:[[{q: 5, p: 40}],[{q: 4, p:41}]], additionalInformation:...}, ... ]
     *
     * convert / map those to array  of array of costItemObject totals per period
     * using xxxCostItemObjectTotaler to get the inner array of values
     * [ [200, 164], ...]
     *
     * total up those array of array values for each period into single array of totals for tree
     * [1200, 1164]
     *
     */
    /*collect array of costItemObjects
     * [ {cost:[ [{...},  [{...}] ], additionalInformation: {...}, ... }, {cost: ..., additionalInformation:..., ...}, {...}, ...]
     */
    let costItemObjs = [];
    this.collectEligibleCostItemObjects(costItemObjs, costItemTree);

    // map that costItemObj array to array of period totals
    // [ [10, 10], [11, 12], ...]
    let costItemTotalsArray = _.map(costItemObjs, (costItemObj) => {
      return this.computeCostItemObjectValue(costItemObj);
    });

    // Sum up the values for each period
    // [21, 22]
    let periodTotalsForCostItemsInTree = _.fill(new Array(numPeriods), 0);
    _.reduce(
      costItemTotalsArray,
      (periodTotalsForCostItemsInTree, costItemPeriodTotals, i) => {
        _.forEach(costItemPeriodTotals, (costItemPeriodTotal, p) => {
          periodTotalsForCostItemsInTree[p] = safeAddNoFixed(
            periodTotalsForCostItemsInTree[p],
            costItemPeriodTotal
          );
        });
        return periodTotalsForCostItemsInTree;
      },
      periodTotalsForCostItemsInTree
    );
    return periodTotalsForCostItemsInTree;
  }

  collectEligibleCostItemObjects(costItemObjArray, costItemNode) {
    if (_.get(costItemNode, COST)) {
      // we have a leaf-node costItemObj
      if (this.isCostItemObjectEligible(costItemNode)) {
        costItemObjArray.push(costItemNode);
      }
    }
    const childNodes = _.get(costItemNode, 'children');
    if (childNodes) {
      _.forEach(childNodes, (childNode) => {
        this.collectEligibleCostItemObjects(costItemObjArray, childNode);
      });
    }
    return;
  }

  isCostItemObjectEligible(costItemObj) {
    const ineligible = _.get(costItemObj, 'additionalInformation.ineligible', false);
    return !ineligible;
  }

  /**
   * Compute the total of all cost objects associated with each BOC value for each period and return object
   * with key for each BOC value whose value itself is an array of numPeriods length and whose values are
   * the total costs for that period and that BOC value.  Also include keys for totalDirectCharges,
   * totalIndirectCharges, and totalCharges with similar arrays representing subtotals / grand totals for
   * each period.
   *
   * @param {*} numPeriods - max number of periods associated with fundingStreamTypes that appear in fundingStreams
   * @param {*} fundingStreams - array of fundingStream objects with the costItem hierarchy for each
   * @returns an object keyed by all the Basis of Cost (BOC) values and they value associated with each
   * of those is an numPeriods-element array with the total value for each period for costObjs with that BOC value.
   * Also sets the following properties in the returned object
   * totalIndirectCharges - same as array for indirectCharges BOC.
   * totalDirectCharges - sum of period totals across all BOC values other than indirectCharges
   * totalCharges - sum of period totals across all BOC values.
   * Example result
   * {
   *   "construction":    [10, 100],
   *   "contractual":     [20, 200],
   *   "equipment":       [30, 300],
   *   "indirectCharges": [1, 10],
   *   "fringeBenefits":  [0, 0],
   *   "other":           [0, 0],
   *   "personnel":       [0, 0],
   *   "supplies":        [0, 0],
   *   "travel":          [0, 0],
   *   "totalIndirectCharges": [ 1,  10],
   *   "totalDirectCharges":   [60, 600],
   *   "totalCharges":         [61, 610],
   * },
   *
   * Caveat, BOC values are stored in the "boc" property of a costObj within a periodCostArray.  Therefore we cannot
   * create a budgetSummary object with a totaler of type actualsHiring or allowablesHiring whose values are stored
   * under additionalInformation and not under the cost property that is an array of periodCostArrays with costObj
   * in them.  In practice, this should be a non-issue as actuals an allowables are for closeout related pages
   * that don't render totals per BOC.  If an actualsHiring or allowablesHiring totaler invokes this method,
   * then there will be 0s for each period value for each BOC.
   */
  buildBudgetSummaryObj(numPeriods, fundingStreams) {
    // if totalerType is actualsHiring or allowablesHiring, then just return empty object, we can't process those.
    if (['default', 'actuals', 'allowables'].indexOf(this.totalerType) == -1) {
      return {};
    }
    let costItemObjs = [];
    this.collectEligibleCostItemObjects(costItemObjs, {children: fundingStreams});
    let costObjsByPeriod = this.collectCostObjectsByPeriod(numPeriods, costItemObjs);
    let totalsPerBocPerPeriod = this.computeTotalsPerBocPerPeriod(
      numPeriods,
      costObjsByPeriod
    );
    let totalIndirectCharges = this.sumBocTotalsByPeriod(
      numPeriods,
      totalsPerBocPerPeriod,
      INDIRECT_CHARGES_BOCS
    );
    let totalDirectCharges = this.sumBocTotalsByPeriod(
      numPeriods,
      totalsPerBocPerPeriod,
      DIRECT_CHARGES_BOCS
    );
    let totalCharges = this.sumBocTotalsByPeriod(numPeriods, totalsPerBocPerPeriod, BOCS);
    let budgetSummary = {
      ...totalsPerBocPerPeriod,
      totalDirectCharges,
      totalIndirectCharges,
      totalCharges,
    };
    return budgetSummary;
  }

  /**
   * Convert the array of costItemObjs to an array of nested arrays (numPeriods of them)
   * each populated with all the costObjs for that period
   * [
   *   [{*costObj*}, ...], // 1st period cost objects
   *   [{*costObj*}, ...], // 2nd period cost objects
   *   ...
   * ]
   * @param {*} numPeriods - determines number of nested arrays
   * @param {*} eligibleCostItemObjs
   */
  collectCostObjectsByPeriod(numPeriods, eligibleCostItemObjs) {
    /* initialize array with numPeriods elements, each of which is an empty array
     */
    const costObjectsByPeriod = [];
    for (let p = 0; p < numPeriods; p++) {
      costObjectsByPeriod[p] = [];
    }

    // iterate through each of the cost *item* objects ...
    _.forEach(eligibleCostItemObjs, (costItemObj, i) => {
      const cost = _.get(costItemObj, COST); // get costItemObj.cost property
      // iterate through each of cost's periods
      _.forEach(cost, (periodCostArray, p) => {
        // iterate through each costObj in periodCostArray
        _.forEach(periodCostArray, (periodCostObj, j) => {
          // add it to the period's array in return object
          costObjectsByPeriod[p].push(periodCostObj);
        });
      });
    });
    return costObjectsByPeriod;
  }

  /**
   * Returns an object with a key for each BOC value whose value is is
   * and array numbers, each number representing the total for a period.
   * For numPeriods = 2 the result would look like
   * {
   *   "construction": [10, 100],
   *   "contractual":  [20, 200],
   *   "equipment":   [30, 300],
   *   ... other BOC keys and 2-element array of numeric values...
   * }
   * Cost objects would be interpreted as appropriate for a default, actuals, allowables totaler
   * {
   *   "boc": "other","quantity": 10,"price": "1000.00","total": 10000,       // original (default) values
   *   "actualQuantity": 15, "actualPrice": "1000.00", "actualTotal": 15000,  // actuals
   *   "scopeOfWorkTotal": "10000.00", "allowedExcessTotal": "1000.00", "disallowedTotal": "4000.00", // allowables
   *   "categorizedSum": 100, "actualAllowedTotal": 100,
   * }
   *
   * For the SAFER hiringOfFirefighters funding stream the actuals and allowables are stored differently.
   * Their actuals and allowables values are not stored within cost objects that contain a "boc" value,
   * but are stored under "additionalInformation" property of a cost *item* object.  A actualsHiring
   * or allowablesHiring totaler instance of this class will result in having 0s for all cost objects.
   * @param {*} numPeriods
   * @param {*} costObjsByPeriod - an array of arrays with nested arrays containing cost objects to be evaluated and totaled
   * @returns - object with key for each BOC value and an numPeriods-sized array of totals per period for that BOC value
   */
  computeTotalsPerBocPerPeriod(numPeriods, costObjsByPeriod) {
    // initial budgetSummary object w/ a numPeriods-element array w/ 0 values for each BOC value
    let sortedBocs = JSON.parse(JSON.stringify(BOCS));
    sortedBocs.sort();

    let totalsByBocByPeriod = _.reduce(
      sortedBocs,
      (budgetSummary, boc) => _.set(budgetSummary, boc, _.fill(new Array(numPeriods), 0)),
      {}
    );
    totalsByBocByPeriod = _.reduce(
      costObjsByPeriod,
      (totByBocByPer, costObjsForPeriod, p) => {
        //p=period
        _.forEach(costObjsForPeriod, (costObj) => {
          const boc = costObj.boc;
          if (_.includes(BOCS, boc)) {
            const bocTotalPath = `${boc}.${p}`; // e.g., "equipment.0"
            const currBocTotal = _.get(totByBocByPer, bocTotalPath);
            const costObjVal = this.computeCostObjectValue(costObj);
            const newBocTotal = safeAddNoFixed(currBocTotal, costObjVal);
            _.set(totByBocByPer, bocTotalPath, newBocTotal);
          }
          return totByBocByPer;
        });
        return totByBocByPer;
      },
      totalsByBocByPeriod
    );
    return totalsByBocByPeriod;
  }

  /**
   * Sum up the period totals of each BOC specified in bocsInTotal
   * whose period totals are contained in totalsByBocByPeriod and
   * are an array of length numPeriods.
   * Example for numPeriods = 2,
   * totalsByBocByPeriod =
   * {
   *   "construction": [10, 100],
   *   "contractual": [20, 200],
   *   "equipment": [30, 300],
   *   "indirectCharges": [1, 10],
   *   ... [0, 0] for fringeBenefits, other, personnel, supplies, travel
   * },
   * when bocsInTotal = ["construction", "contractual", "equipment"]
   * then result is [60, 600]
   * when bocsInTotal = ["indirectCharges"]
   * then result is [1, 10]
   * when bocsInTotal = [
   *   "construction", "contractual", "equipment", "indirectCharges",
   *   "fringeBenefits", "other", "personnel", "supplies", "travel"
   * ]
   * then result is [61, 610]
   *
   * @param {*} numPeriods - expected length of array for each property in totalsByBocByPeriod
   * @param {*} totalsByBocByPeriod - object containing keys for each of the BOC values and associated array value
   * @param {*} bocsInTotal - which BOC values we want to sum up
   * @returns an array of length numPeriods containing the total for each period
   */
  sumBocTotalsByPeriod(numPeriods, totalsByBocByPeriod, bocsInTotal) {
    let totalsByPeriod = _.reduce(
      _.range(numPeriods),
      (periodTotals, period) => {
        periodTotals.push(
          _.reduce(
            bocsInTotal,
            (periodTotalOfBocs, boc) => {
              const bocTotal = _.get(totalsByBocByPeriod, `${boc}.${period}`);
              const newPeriodTotal = safeAddNoFixed(periodTotalOfBocs, bocTotal);
              return newPeriodTotal;
            },
            0
          )
        );
        return periodTotals;
      },
      []
    );
    return totalsByPeriod;
  }
}

export default CostItemObjectTotaler;
